import { PageLoader } from '@customerio/pluma-components/react';
import SignupPage from '@/pages/signup/index.page';
import type { SignupPageProps } from '@/pages/signup/types';
import {
  hasSignupPublicFlag,
  SignupPublicFeatureFlag,
  type SignupPublicFlags,
} from '@/services/signup/public-flags-store';
import { useSignupPublicFlags } from '@/services/signup/public-flags';
import { Navigate } from '@/utils/navigate';
import { JourneysLegacyAuthRefreshRoute, JourneysLegacyRoute } from './-components';

function isSignupMigrationEnabled(publicFlags: SignupPublicFlags): boolean {
  return hasSignupPublicFlag(publicFlags, SignupPublicFeatureFlag.SignupMigration);
}

/**
 * Decides which app serves the signup funnel, on the `signup-migration`
 * rollout flag. The funnel is pre-auth, so the gate is a *public* feature flag
 * (see `services/signup/public-flags.ts`); route ownership
 * (`route-ownership.ts`) is gated on the same flag so the ownership answer and
 * the rendered app flip together.
 *
 * Holds render until the flag settles so exactly one app ever paints: the
 * store resolves once per load (a failed fetch settles all-off), so the
 * decision can't flip after the first paint.
 */
export function SignupRouteGate(props: SignupPageProps) {
  const publicFlags = useSignupPublicFlags();

  if (publicFlags == null) {
    return <PageLoader />;
  }

  // Flag off: the embedded app serves /signup, exactly as it does for every
  // other URL it owns.
  if (!isSignupMigrationEnabled(publicFlags)) {
    return <JourneysLegacyAuthRefreshRoute />;
  }

  return <SignupPage {...props} />;
}

/**
 * Serves the legacy `/users/register` and `/users/signup` aliases on the same
 * rollout flag: flag off, the embedded app performs its own redirect to
 * `/signup`; flag on, redirect within the shell.
 */
export function LegacySignupRedirect() {
  const publicFlags = useSignupPublicFlags();

  if (publicFlags == null) {
    return <PageLoader />;
  }

  if (!isSignupMigrationEnabled(publicFlags)) {
    return <JourneysLegacyRoute />;
  }

  // Preserve query params (promo codes, marketing attribution) the legacy link
  // carried. A plain Navigate is fine here: this branch only runs while
  // /signup is Hydra-owned.
  const search = Object.fromEntries(new URLSearchParams(window.location.search));
  return <Navigate to="/signup" replace={true} search={search} />;
}
